import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useT } from '../context/AppContext';
import Sidebar from '../components/Sidebar';
import Notification from '../components/Notification';
import axios from 'axios';
import './AdminPage.css';

const AdminPage = ({ tab: initialTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tr = useT();
  const [activeTab, setActiveTab] = useState(initialTab || 'stats');
  const [notification, setNotification] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docFilter, setDocFilter] = useState('all');

  const showNotif = (message, type = 'success') => setNotification({ message, type });

  useEffect(() => {
    if (location.pathname.includes('users'))     setActiveTab('users');
    if (location.pathname.includes('documents')) setActiveTab('documents');
    if (location.pathname.includes('stats'))     setActiveTab('stats');
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === 'stats')     loadStats();
    if (activeTab === 'users')     loadUsers();
    if (activeTab === 'documents') loadDocuments();
  }, [activeTab]); // eslint-disable-line

  const loadStats = async () => {
    setStatsLoading(true);
    try { const res = await axios.get('/user/admin/stats'); setStats(res.data.stats); }
    catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setStatsLoading(false); }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try { const res = await axios.get('/user/admin/users'); setUsers(res.data.users || []); }
    catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setUsersLoading(false); }
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try { const res = await axios.get('/pdf/admin/all'); setDocuments(res.data.pdfs || []); }
    catch { showNotif(tr('erreur_chargement'), 'error'); }
    finally { setDocumentsLoading(false); }
  };

  const handleToggleUser = async (userId, isActive) => {
    try {
      await axios.put(`/user/admin/users/${userId}/toggle`, { isActive: !isActive });
      showNotif(`${tr('statut')} mis à jour !`);
      loadUsers();
    } catch (err) { showNotif(err.response?.data?.message || tr('erreur_sauvegarde'), 'error'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(tr('supprimer') + ' ?')) return;
    try { await axios.delete(`/user/admin/users/${userId}`); showNotif(tr('client_supprime')); loadUsers(); }
    catch (err) { showNotif(err.response?.data?.message || tr('erreur_suppression'), 'error'); }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm(tr('supprimer') + ' ?')) return;
    try { await axios.delete(`/pdf/${id}`); showNotif(tr('client_supprime')); loadDocuments(); }
    catch { showNotif(tr('erreur_suppression'), 'error'); }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredDocs = documents.filter(d => {
    const matchSearch = (d.fileName || '').toLowerCase().includes(docSearch.toLowerCase()) ||
      (d.User?.email || '').toLowerCase().includes(docSearch.toLowerCase());
    return matchSearch && (docFilter === 'all' || d.type === docFilter);
  });

  const tabs = [
    { id: 'stats',     icon: 'fa-chart-bar',  label: tr('statistiques'), path: '/admin/stats'     },
    { id: 'users',     icon: 'fa-users',       label: tr('utilisateurs'), path: '/admin/users'     },
    { id: 'documents', icon: 'fa-folder-open', label: tr('documents_nav'),path: '/admin/documents' },
  ];

  return (
    <div className="page-with-sidebar">
      <Sidebar />
      {notification && <Notification type={notification.type} message={notification.message} onClose={() => setNotification(null)} />}

      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1><i className="fas fa-shield-alt" style={{ color: 'var(--accent-start)' }}></i> {tr('administration')}</h1>
            <p>{tr('gerer_users_docs')}</p>
          </div>
          <div className="admin-header-badge">
            <i className="fas fa-crown"></i> {tr('acces_admin')}
          </div>
        </div>

        <div className="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-tab ${activeTab === tab.id ? 'admin-tab-active' : ''}`}
              onClick={() => { setActiveTab(tab.id); navigate(tab.path); }}
            >
              <i className={`fas ${tab.icon}`}></i> {tab.label}
            </button>
          ))}
        </div>

        {/* STATS */}
        {activeTab === 'stats' && (
          <div className="admin-section">
            {statsLoading ? (
              <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i> {tr('chargement')}</div>
            ) : stats ? (
              <div className="admin-stats-grid">
                {[
                  { icon: 'fa-users',        label: 'Total ' + tr('utilisateurs'), value: stats.totalUsers,    color: '#667eea' },
                  { icon: 'fa-user-check',   label: tr('statut') + ' actifs',      value: stats.activeUsers,   color: '#10b981' },
                  { icon: 'fa-user-times',   label: tr('statut') + ' inactifs',    value: stats.inactiveUsers, color: '#ef4444' },
                  { icon: 'fa-file-alt',     label: 'Total ' + tr('documents_nav'),value: stats.totalDocs,     color: '#f59e0b' },
                  { icon: 'fa-file-invoice', label: tr('factures_nav'),            value: stats.totalInvoices, color: '#3b82f6' },
                  { icon: 'fa-id-card',      label: 'CV',                          value: stats.totalCVs,      color: '#8b5cf6' },
                ].map((s, i) => (
                  <div key={i} className="admin-stat-card">
                    <div className="admin-stat-icon" style={{ background: `${s.color}20`, color: s.color }}>
                      <i className={`fas ${s.icon}`}></i>
                    </div>
                    <div className="admin-stat-content">
                      <h3>{s.value ?? '—'}</h3>
                      <p>{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-empty"><i className="fas fa-chart-bar"></i><p>{tr('aucune_notification')}</p></div>
            )}
          </div>
        )}

        {/* USERS */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="admin-toolbar">
              <div className="admin-search-wrap">
                <i className="fas fa-search"></i>
                <input type="text" className="admin-search" placeholder={tr('rechercher_utilisateur')} value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              </div>
              <span className="admin-count">{filteredUsers.length} {tr('utilisateurs').toLowerCase()}</span>
            </div>
            {usersLoading ? (
              <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i> {tr('chargement')}</div>
            ) : filteredUsers.length === 0 ? (
              <div className="admin-empty"><i className="fas fa-users-slash"></i><p>{tr('aucun_utilisateur')}</p></div>
            ) : (
              <div className="admin-card">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{tr('utilisateur')}</th>
                      <th>{tr('email')}</th>
                      <th>{tr('role')}</th>
                      <th>{tr('statut')}</th>
                      <th>{tr('inscription')}</th>
                      <th>{tr('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div className="admin-user-cell">
                            <div className="admin-user-avatar">{u.name?.charAt(0).toUpperCase()}</div>
                            <strong>{u.name}</strong>
                          </div>
                        </td>
                        <td>{u.email}</td>
                        <td>
                          <span className={`admin-badge ${u.role === 'admin' ? 'badge-admin' : 'badge-user'}`}>
                            {u.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-badge ${u.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {u.isActive ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="admin-actions">
                            <button
                              className={`admin-action-btn ${u.isActive ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggleUser(u.id, u.isActive)}
                              disabled={u.role === 'admin'}
                            >
                              <i className={`fas ${u.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                            </button>
                            <button
                              className="admin-action-btn btn-danger"
                              onClick={() => handleDeleteUser(u.id)}
                              disabled={u.role === 'admin'}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab === 'documents' && (
          <div className="admin-section">
            <div className="admin-toolbar">
              <div className="admin-search-wrap">
                <i className="fas fa-search"></i>
                <input type="text" className="admin-search" placeholder={tr('rechercher_document')} value={docSearch} onChange={e => setDocSearch(e.target.value)} />
              </div>
              <div className="admin-filter">
                {['all', 'invoice', 'cv'].map(f => (
                  <button key={f} className={`admin-filter-btn ${docFilter === f ? 'active' : ''}`} onClick={() => setDocFilter(f)}>
                    {f === 'all' ? tr('tous') : f === 'invoice' ? tr('facture') : 'CV'}
                  </button>
                ))}
              </div>
              <span className="admin-count">{filteredDocs.length} {tr('documents_nav').toLowerCase()}</span>
            </div>
            {documentsLoading ? (
              <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i> {tr('chargement')}</div>
            ) : filteredDocs.length === 0 ? (
              <div className="admin-empty"><i className="fas fa-folder-open"></i><p>{tr('aucun_document_admin')}</p></div>
            ) : (
              <div className="admin-card">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{tr('fichier')}</th>
                      <th>{tr('type')}</th>
                      <th>{tr('proprietaire')}</th>
                      <th>{tr('date')}</th>
                      <th>{tr('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocs.map(doc => (
                      <tr key={doc.id}>
                        <td>
                          <div className="admin-doc-cell">
                            <i className={`fas ${doc.type === 'invoice' ? 'fa-file-invoice' : 'fa-file-alt'}`} style={{ color: 'var(--accent-start)', marginRight: '0.5rem' }}></i>
                            {doc.fileName}
                          </div>
                        </td>
                        <td>
                          <span className={`admin-badge ${doc.type === 'invoice' ? 'badge-invoice' : 'badge-cv'}`}>
                            {doc.type === 'invoice' ? tr('facture') : 'CV'}
                          </span>
                        </td>
                        <td>{doc.User?.email || '—'}</td>
                        <td>{new Date(doc.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button className="admin-action-btn btn-danger" onClick={() => handleDeleteDocument(doc.id)}>
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;